import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const MAX_QUOTE_CHARS = 2000;
const ASK_BUTTON_WIDTH = 56;

function preventMouseDownDefault(event: React.MouseEvent) {
  event.preventDefault();
}

function selectedTextInContainer(container: HTMLElement | null): string {
  const selection = window.getSelection();
  if (!selection || selection.isCollapsed || selection.rangeCount === 0) return '';

  const range = selection.getRangeAt(0);
  const anchor = selection.anchorNode;
  const focus = selection.focusNode;
  if (!container || !anchor || !focus) return '';
  if (!container.contains(anchor) || !container.contains(focus)) return '';

  const node = range.commonAncestorContainer;
  const element = node instanceof Element ? node : node.parentElement;
  if (element?.closest('textarea, input, [data-selection-ask-ignore]')) return '';

  return selection.toString().replace(/\u00a0/g, ' ').trim();
}

function lastSelectedGlyphRect(range: Range): DOMRect | null {
  const root = range.commonAncestorContainer;
  const walkerRoot = root.nodeType === Node.ELEMENT_NODE ? root : root.parentNode;
  if (!walkerRoot) return null;

  const rects: DOMRect[] = [];
  const walker = document.createTreeWalker(walkerRoot, NodeFilter.SHOW_TEXT);
  let node = walker.nextNode();
  while (node) {
    const current = node;
    node = walker.nextNode();
    if (!range.intersectsNode(current)) continue;
    const text = current as Text;
    const start = current === range.startContainer ? range.startOffset : 0;
    const end = current === range.endContainer ? range.endOffset : text.length;
    if (end <= start) continue;
    const piece = document.createRange();
    piece.setStart(text, Math.max(0, start));
    piece.setEnd(text, Math.min(text.length, end));
    rects.push(...Array.from(piece.getClientRects()));
  }

  const ink = rects.filter((item) => item.width > 0 && item.height > 0);
  if (ink.length === 0) return null;
  return ink.reduce((best, item) => (
    item.bottom > best.bottom || (item.bottom === best.bottom && item.right > best.right)
      ? item
      : best
  ));
}

export function formatAskQuote(text: string): string {
  return text.trim().slice(0, MAX_QUOTE_CHARS);
}

export function buildQuotedPrompt(quote: string, question: string): string {
  const excerpt = `Regarding this excerpt:\n\n"""\n${quote.trim()}\n"""`;
  const trimmedQuestion = question.trim();
  return trimmedQuestion ? `${excerpt}\n\n${trimmedQuestion}` : excerpt;
}

export function AskQuoteButton({
  buttonRef,
  className,
  style,
  onClick,
}: {
  buttonRef?: React.Ref<HTMLButtonElement>;
  className?: string;
  style?: React.CSSProperties;
  onClick: React.MouseEventHandler<HTMLButtonElement>;
}) {
  return (
    <Button
      ref={buttonRef}
      type="button"
      variant="ghost"
      data-selection-ask-ignore="true"
      aria-label="Ask about this"
      className={cn(
        'h-7 select-none gap-1 rounded-full border border-slate-200 bg-white px-2 text-xs font-medium text-slate-700 shadow-sm transition-none hover:bg-slate-50',
        className,
      )}
      style={style}
      onMouseDown={preventMouseDownDefault}
      onClick={onClick}
    >
      <img src="/logo-icon.svg" alt="" className="h-3.5 w-3.5" />
      Ask
    </Button>
  );
}

interface SelectionAskButtonProps {
  containerRef: React.RefObject<HTMLElement | null>;
  onAsk: (quotedText: string) => void;
}

export function SelectionAskButton({ containerRef, onAsk }: SelectionAskButtonProps) {
  const [anchor, setAnchor] = useState<{ x: number; y: number; text: string } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const pointerSelectingRef = useRef(false);

  const hide = useCallback(() => setAnchor(null), []);
  const scrollFrameRef = useRef(0);

  const updateFromSelection = useCallback(() => {
    const container = containerRef.current;
    const text = selectedTextInContainer(container);
    if (!text) {
      setAnchor(null);
      return;
    }

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      setAnchor(null);
      return;
    }

    const range = selection.getRangeAt(0);
    const rect = lastSelectedGlyphRect(range);
    if (!rect) {
      setAnchor(null);
      return;
    }

    const gap = 8;
    const inView = rect.bottom > 0 && rect.top < window.innerHeight;
    if (!inView) {
      setAnchor(null);
      return;
    }

    let x = rect.right + gap;
    if (x + ASK_BUTTON_WIDTH > window.innerWidth - 8) {
      x = rect.right - ASK_BUTTON_WIDTH - gap;
    }
    const next = {
      x: Math.max(8, x),
      y: (rect.top + rect.bottom) / 2,
      text,
    };
    setAnchor((prev) => (
      prev
      && Math.abs(prev.x - next.x) < 0.5
      && Math.abs(prev.y - next.y) < 0.5
      && prev.text === next.text
        ? prev
        : next
    ));
  }, [containerRef]);

  useEffect(() => {
    const handlePointerDown = (event: PointerEvent) => {
      if (buttonRef.current?.contains(event.target as Node)) return;
      pointerSelectingRef.current = true;
      setAnchor(null);
    };

    const handlePointerUp = (event: PointerEvent) => {
      pointerSelectingRef.current = false;
      if (buttonRef.current?.contains(event.target as Node)) return;
      window.requestAnimationFrame(updateFromSelection);
    };

    const handleSelectionChange = () => {
      if (pointerSelectingRef.current) return;
      updateFromSelection();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') hide();
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('pointerup', handlePointerUp);
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('selectionchange', handleSelectionChange);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('pointerup', handlePointerUp);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('selectionchange', handleSelectionChange);
    };
  }, [hide, updateFromSelection]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;
    const handleScrollOrResize = () => {
      if (pointerSelectingRef.current) return;
      if (scrollFrameRef.current) return;
      scrollFrameRef.current = window.requestAnimationFrame(() => {
        scrollFrameRef.current = 0;
        updateFromSelection();
      });
    };
    container.addEventListener('scroll', handleScrollOrResize, { passive: true });
    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);
    return () => {
      container.removeEventListener('scroll', handleScrollOrResize);
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
      if (scrollFrameRef.current) window.cancelAnimationFrame(scrollFrameRef.current);
    };
  }, [containerRef, updateFromSelection]);

  const handleAskClick = useCallback(() => {
    if (!anchor) return;
    onAsk(anchor.text);
    window.getSelection()?.removeAllRanges();
    hide();
  }, [anchor, hide, onAsk]);

  if (!anchor) return null;

  return createPortal(
    <AskQuoteButton
      buttonRef={buttonRef}
      className="fixed z-50"
      style={{ left: anchor.x, top: anchor.y, transform: 'translateY(-50%)' }}
      onClick={handleAskClick}
    />,
    document.body,
  );
}
